import React, { useEffect, useState, useCallback } from "react";
import { WifiOff, CloudOff, RefreshCw } from "lucide-react";

// Global, always-mounted banner reflecting offline + backend-unreachable states.
// Drafts in chat inputs are preserved by the chat screens themselves (localStorage);
// this component only surfaces connectivity and a retry affordance.
export default function ConnectionStatus() {
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && !navigator.onLine);
  const [backendDown, setBackendDown] = useState(false);

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    const down = () => setBackendDown(true);
    const ok = () => setBackendDown(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener("maziara:backend-down", down);
    window.addEventListener("maziara:backend-ok", ok);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener("maziara:backend-down", down);
      window.removeEventListener("maziara:backend-ok", ok);
    };
  }, []);

  const retry = useCallback(() => window.location.reload(), []);

  if (!offline && !backendDown) return null;

  const isOffline = offline;
  return (
    <div data-testid={isOffline ? "offline-banner" : "backend-down-banner"}
      className="fixed left-0 right-0 top-0 z-[60] flex items-center justify-center gap-2 bg-[#361217] px-4 py-2 text-center text-sm text-[#F4D9D6]">
      {isOffline ? <WifiOff className="h-4 w-4 shrink-0" /> : <CloudOff className="h-4 w-4 shrink-0" />}
      <span>
        {isOffline
          ? "You're offline. Your message drafts are saved — we'll reconnect automatically."
          : "We're having trouble reaching Maziara. Your drafts are safe."}
      </span>
      {!isOffline && (
        <button onClick={retry} className="ml-1 inline-flex items-center gap-1 rounded-full bg-[#5C1D24] px-3 py-1 text-xs text-white hover:bg-[#6d2229]">
          <RefreshCw className="h-3 w-3" /> Retry
        </button>
      )}
    </div>
  );
}
